import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { participantShare } from '../format';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import Tabs from '../components/Tabs';

/**
 * Purchase detail.
 *
 * A purchase holds one *or more* products, which is the thing the old page gets
 * right and is easy to lose: with two products it renders every product field
 * twice, suffixed `#1` and `#2`, and gives each its own Participants and Fields
 * tabs. Flattening that would quietly show one product's price as the
 * purchase's.
 *
 * So the products are a picker when there is more than one, and the tabs below
 * follow the selected product. With a single product the picker is not rendered
 * at all — nothing to choose.
 *
 * Participants and Fields load per product and only when their tab is opened;
 * they are separate endpoints and most visits never look at them.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const money = value => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : `${n.toFixed(2)} DKK`;
};

function SubTable({ rows, columns, empty }) {
  if (rows === null) return <p className="next-table__note next-muted">Loading…</p>;
  if (rows.length === 0) return <p className="next-table__note next-muted">{empty}</p>;

  return (
    <div className="next-table-wrap">
      <table className="next-table next-table--wide next-table--stack">
        <thead>
          <tr>{columns.map(([, label]) => <th key={label}>{label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.Id || index}>
              {columns.map(([key, label, kind], columnIndex) => (
                <td
                  key={label}
                  data-label={columnIndex === 0 ? '' : label}
                  className={kind === 'date' ? 'next-nowrap next-muted' : undefined}
                >
                  {kind === 'id'
                    ? <Guid value={row[key]} />
                    : kind === 'date'
                      ? fmtDate(row[key])
                      : kind === 'share'
                        ? (participantShare(row[key]) ?? '—')
                        : (row[key] === '' || row[key] == null ? '—' : String(row[key]))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

SubTable.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.array.isRequired,
  empty: PropTypes.string.isRequired
};

const PurchaseInternal = ({ api }) => {
  const { purchaseId } = useParams();

  const [purchase, setPurchase] = useState(null);
  const [failed, setFailed] = useState(false);
  const [productIndex, setProductIndex] = useState(0);
  const [tab, setTab] = useState('info');
  const [participants, setParticipants] = useState(null);
  const [fields, setFields] = useState(null);

  const load = useCallback(async () => {
    try {
      setPurchase(await api.purchases.getPurchase(purchaseId));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, purchaseId]);

  useEffect(() => {
    load();
  }, [load]);

  const products = purchase?.Products ?? [];
  const product = products[productIndex];

  // Cleared whenever the selected product changes, so the tab never shows the
  // previous product's rows while the new ones are in flight.
  useEffect(() => {
    setParticipants(null);
    setFields(null);
  }, [productIndex]);

  useEffect(() => {
    if (!product) return;

    if (tab === 'participants' && participants === null) {
      api.purchases.searchParticipants({ filters: { purchaseProductId: product.Id }, resultsCount: 100 })
        .then(data => setParticipants(Array.isArray(data) ? data : (data?.Participants ?? [])))
        .catch(() => setParticipants([]));
    }

    if (tab === 'fields' && fields === null) {
      api.purchases.searchFields({ filters: { purchaseProductId: product.Id } })
        .then(data => setFields(Array.isArray(data) ? data : (data?.Fields ?? [])))
        .catch(() => setFields([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, product, participants, fields]);

  if (failed) {
    return (
      <div className="next-page">
        <div className="next-note next-note--error">
          Could not load this purchase.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!purchase) return <div className="next-page next-muted">Loading…</div>;

  const purchaseFields = [
    ['User', [purchase.UserName, purchase.UserEmail].filter(Boolean).join(' — ')],
    ['User id', { guid: purchase.UserId, href: `/next/user/${purchase.UserId}`, internal: true }],
    ...(purchase.PaymentMethodName ? [['Paid with', purchase.PaymentMethodName]] : []),
    ['Payment method', { guid: purchase.PaymentMethod, href: `/paymentmethod/creditcard/${purchase.PaymentMethod}` }],
    ['Created at (CET)', fmtDate(purchase.CreatedAtCet)],
    ...(products.length > 1 ? [['Products', products.length]] : [])
  ];

  const productFields = product ? [
    ['Product id', { guid: product.ProductId, href: `/product/${product.ProductId}` }],
    ['Purchase product id', { guid: product.Id }],
    ['Product name', product.ProductName],
    ['Quantity', product.Quantity],
    ['Purchase text', product.Text],
    ['Base price', money(product.BasePrice)],
    ['Total price', money(product.GrandTotal)],
    ['Billing at (CET)', fmtDate(product.BillingAtCet)],
    ['Latest cancellation at (CET)', fmtDate(product.LatestCancellationAtCet)],
    ['Occurs at (CET)', fmtDate(product.OccursAtCet)]
  ] : [];

  const renderFacts = list => (
    <div className="next-facts next-facts--single">
      {list.map(([label, value]) => (
        <div className="next-fact" key={label}>
          <span className="next-fact__label">{label}</span>
          {value && typeof value === 'object' && 'guid' in value ? (
            value.internal
              ? <Guid value={value.guid} to={value.href} />
              : <Guid value={value.guid} href={value.href} />
          ) : (
            <span className="next-fact__value">
              {value === '' || value == null ? '—' : value}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/purchases">← All purchases</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{product?.ProductName || 'Purchase'}</h1>
          {product?.Status && <StatusPill status={product.Status}>{product.Status}</StatusPill>}
        </div>
        <span className="next-detail__id">{purchase.Id}</span>
      </header>

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Purchase</h2></div>
            {renderFacts(purchaseFields)}
          </section>

          {products.length > 1 && (
            <section className="next-card">
              <div className="next-card__head">
                <h2>Products</h2>
                <span className="next-muted">this purchase holds {products.length}</span>
              </div>
              <Tabs
                tabs={products.map((p, index) => ({
                  key: String(index),
                  label: p.ProductName || `Product ${index + 1}`
                }))}
                active={String(productIndex)}
                onChange={key => setProductIndex(Number(key))}
              />
            </section>
          )}

          <section className="next-card">
            <Tabs
              tabs={[
                { key: 'info', label: 'Basic info' },
                { key: 'participants', label: 'Participants', count: participants?.length },
                { key: 'fields', label: 'Fields', count: fields?.length }
              ]}
              active={tab}
              onChange={setTab}
            />

            {tab === 'info' && (product
              ? renderFacts(productFields)
              : <p className="next-table__note next-muted">This purchase has no product.</p>)}

            {tab === 'participants' && (
              <SubTable
                rows={participants}
                columns={[
                  ['Id', 'Id', 'id'],
                  ['InviteeName', 'Name'],
                  // The same field the participants list shows as a share, and it
                  // arrives here as a bare decimal instead of a `Percentage` — so
                  // without this the two screens print 0.5 and 50% for one
                  // participant, with nothing saying they are the same number.
                  ['ParticipantsIndex', 'Share', 'share'],
                  ['Reference', 'Reference'],
                  ['Type', 'Type'],
                  ['Status', 'Status']
                ]}
                empty="No participant on this product."
              />
            )}

            {tab === 'fields' && (
              <SubTable
                rows={fields}
                columns={[
                  ['Id', 'Id', 'id'],
                  ['Key', 'Key'],
                  ['Value', 'Value'],
                  ['CreatedAtCet', 'Created at (CET)', 'date']
                ]}
                empty="No field on this product."
              />
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                purchase.UserId && {
                  label: 'User',
                  hint: purchase.UserName || purchase.UserEmail,
                  to: `/next/user/${purchase.UserId}`
                },
                { label: 'Transactions', hint: 'money on this purchase', to: `/next/transactions?purchaseId=${purchaseId}` },
                { label: 'Signups', hint: 'bookings it paid for', to: `/next/signups?purchaseId=${purchaseId}` },
                { label: 'Participants', hint: 'everyone on it', to: `/next/participants?purchaseId=${purchaseId}` },
                // Per product, because that is what the endpoint filters on.
                product && {
                  label: 'This product’s participants',
                  hint: product.ProductName,
                  to: `/next/participants?purchaseProductId=${product.Id}`
                },
                purchase.PaymentMethod && {
                  label: 'Payment method',
                  href: `/paymentmethod/creditcard/${purchase.PaymentMethod}`
                },
                product?.ProductId && { label: 'Product', hint: product.ProductName, href: `/product/${product.ProductId}` }
              ]}
            />
          </section>

          {/*
            The design's Actions panel says "None", and the old page has none
            either — a purchase is changed through its transactions, not here.
            The panel is left out rather than rendered empty.
          */}
        </aside>
      </div>
    </div>
  );
};

PurchaseInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Purchase = withApi(PurchaseInternal);

Purchase.requiresLogin = true;

export default Purchase;
